import {ActionTree, Module, GetterTree, MutationTree} from "vuex";
import orderBy from 'lodash/orderBy';
import Vue from 'vue';
import http from "@/axios";
import {RootState} from "@/store/types";
import {ChatState, MessageChat} from "./types";
import {MESSAGES_PER_FETCH} from './constants';

const namespaced: boolean = false;

const state: ChatState = {
  messagesByRoom: {},
};

const getters: GetterTree<ChatState, any> = {
  chatMessages: (st) => (roomType: string) => st.messagesByRoom?.[roomType] ?? [],
  chatMessageIds: (st) => (roomType: string) => (st.messagesByRoom?.[roomType] ?? []).map((m: MessageChat) => m.id),
  chatLatestMessage: (st) => (roomType: string) => {
    const messages = st.messagesByRoom?.[roomType] ?? [];
    return messages.length ? messages[0] : undefined;
  },
  chatEarliestMessage: (st) => (roomType: string) => {
    const messages = st.messagesByRoom?.[roomType] ?? [];
    return messages.length ? messages[messages.length - 1] : undefined;
  },
};

const mutations: MutationTree<ChatState> = {
  SET_CHAT_RESPONSE_DATA(st: ChatState, payload: { roomType: string, data: MessageChat[] }) {
    const roomType = payload?.roomType;
    if (!roomType) return;
    const data = payload?.data ?? [];
    if (!st.messagesByRoom[roomType]) {
      Vue.set(st.messagesByRoom, roomType, []);
    }
    const current = st.messagesByRoom[roomType] ?? [];
    const messages = [ ...current, ...data ];
    Vue.set(st.messagesByRoom, roomType, orderBy(messages, ['time'], ['desc']));
  },
  SET_CHAT_LATEST_MESSAGES(st: ChatState, payload: { roomType: string, data: MessageChat[] }) {
    const roomType = payload?.roomType;
    if (!roomType) return;
    const data = payload?.data ?? [];
    if (!st.messagesByRoom[roomType]) {
      Vue.set(st.messagesByRoom, roomType, []);
    }
    const current = st.messagesByRoom[roomType] ?? [];
    const messages = [ ...data, ...current ];
    Vue.set(st.messagesByRoom, roomType, orderBy(messages, ['time'], ['desc']));
  },
  CLEAR_CHAT_MESSAGES(st: ChatState, payload: { roomType: string }) {
    const roomType = payload?.roomType;
    if (!roomType) return;
    Vue.set(st.messagesByRoom, roomType, []);
  },
  DELETE_CHAT_MESSAGE(st: ChatState, payload: { roomType: string, id: number }) {
    const roomType = payload?.roomType;
    if (!roomType) return;
    const id = payload?.id;
    if (typeof id !== 'number') return;
    const current = st.messagesByRoom?.[roomType] ?? [];
    Vue.set(st.messagesByRoom, roomType, current.filter(m => m.id !== id));
  }
};

const actions: ActionTree<ChatState, any> = {

  fetchChatList({ commit, state }, payload?: { roomType: string }) {
    if (!payload?.roomType) {
      return Promise.reject(new Error('chat.fetchChatList: missing payload.roomType'));
    }
    const { roomType } = payload;
    const chatMessages: MessageChat[] = state.messagesByRoom?.[roomType] ?? [];
    const chatMessageIds: number[] = chatMessages.map((m: MessageChat) => m.id);
    const endpoint = 'api/chat/' + roomType + '/list';
    const params = { from: 0, to: MESSAGES_PER_FETCH }
    if (chatMessages.length) {
      params.from = chatMessages.length;
      params.to = params.from + MESSAGES_PER_FETCH;
    }
    return http.get(endpoint, { params })
      .then(r => r.data)
      .then((data: MessageChat[]) => {
        const filteredMessages = data.filter((m: MessageChat) => {
          return !chatMessageIds.includes(m.id);
        });
        commit('SET_CHAT_RESPONSE_DATA', { roomType, data: filteredMessages });
      })
      .catch(function (error) {
        return Promise.reject(error);
      });
  },

  fetchLatestChatMessages({commit, state, dispatch}, payload: { roomType: string }) {
    const { roomType } = payload;
    const chatLatestMessage = (state.messagesByRoom?.[roomType] ?? [])[0];
    if (!chatLatestMessage) return dispatch('fetchChatList', { roomType });
    const afterId = chatLatestMessage.id;
    const endpoint = 'api/chat/' + roomType + '/list/latest';
    const params = { afterId };
    return http.get(endpoint, { params })
      .then(r => r.data)
      .then((data: MessageChat[]) => {
        commit('SET_CHAT_LATEST_MESSAGES', { roomType, data });
      })
      .catch(function (error) {
        return Promise.reject(error);
      });
  },

  clearChatMessages({ commit }, payload: { roomType: string }) {
    commit('CLEAR_CHAT_MESSAGES', { roomType: payload.roomType });
  },

  postChatMessage(store, payload: { roomType: string, text: string }) {
    const { roomType, text } = payload;
    const endpoint = 'api/chat/' + roomType + '/send';
    return http.post(endpoint, text);
  },

  deleteChatMessage({ commit }, payload: { roomType: string, id: number }) {
    const { roomType, id } = payload;
    return http.post('/api/chat/' + roomType + '/message/' + id + '/delete');
  }
}

export const chatModule: Module<ChatState, RootState> = {
  namespaced,
  state,
  mutations,
  actions,
  getters,
};
